/**
 * svgColorScheme — color scheme mutations for the InfoMode plan renderer.
 *
 * Applied by renderers on a *clone* of the cached SVGElement (or in-place for
 * the re-apply-without-remount path). Sets inline `style.fill` on each
 * `path.compartment` according to the active COLOR_SCHEME.
 *
 * Layer mechanic:
 *   Layer 1: CSS (_plan-base.scss) → baseline room fills via var()
 *   Layer 2: this module → inline style.fill on each path
 *   Inline style always wins over CSS class rules, so any non-null fill here
 *   overrides Layer 1 without specificity fights.
 *
 * MONO scheme: inline styles cleared — falls back to Layer 1 CSS vars.
 */

import { mixHex } from '../../../shared/utils/colorUtils.js'

import { COLOR_SCHEME } from '../constants/colorSchemes.js'

// ── CSS var helper ────────────────────────────────────────────────────────────

/**
 * Reads a computed CSS custom property value from the current app root.
 * Used to resolve --bim-color-* tokens at call time (after useThemeVars has run).
 *
 * @param {string} name — e.g. '--bim-color-zone-day'
 * @param {HTMLElement|null} [rootElement]
 * @returns {string} resolved value, or '' if not set
 */
function _getCssVar(name, rootElement = null) {
  if (!rootElement) return ''
  return getComputedStyle(rootElement).getPropertyValue(name).trim()
}

/**
 * Converts a camelCase SVG class id like 'idLivingRoomWithAnnex'
 * to a kebab-case token like 'living-room-with-annex'.
 * Strips the leading 'id' prefix.
 *
 * @param {string} nameCad
 * @returns {string}
 */
function _nameCadToToken(nameCad) {
  return nameCad
    .replace(/^id/, '')
    .replace(/([A-Z])/g, (c) => `-${c.toLowerCase()}`)
    .replace(/^-/, '')
}

/**
 * Returns the brightness bucket for a compartment.
 * 'none' — no windows; 'low' — below legal minimum; number 0–9 — gradient decile.
 *
 * @param {number} windowArea
 * @param {number} floorArea
 * @returns {'none'|'low'|number}
 */
export function getBrightnessBucket(windowArea, floorArea) {
  if (!windowArea) return 'none'
  const ratio = floorArea > 0 ? (windowArea / floorArea) * 8 : 0
  if (ratio < 1) return 'low'
  const t = Math.min(1, (ratio - 1) / 2)
  return Math.min(9, Math.floor(t * 10))
}

// ── Color scheme mutations ────────────────────────────────────────────────────

/**
 * applyColorSchemeMutations — sets inline `style.fill` on each `path.compartment`
 * for the active InfoMode color scheme.
 *
 * Data params:
 * @param {SVGElement}      svgElement
 * @param {string}          scheme               — COLOR_SCHEME value
 * @param {object|null}     compartmentTypes     — raw compartments.json (has .compartments_types map)
 * @param {Array}           productCompartments  — product.compartments array (number, area_original, window_area)
 * @param {Array}           allProducts          — full products list for COMPARE decile calc
 */
export function applyColorSchemeMutations(
  svgElement,
  scheme,
  compartmentTypes = null,
  productCompartments = [],
  allProducts = [],
  rootElement = null,
) {
  const paths = svgElement.querySelectorAll('path.compartment')

  // MONO — clear any previously set inline fills, let CSS vars take over
  if (scheme === COLOR_SCHEME.MONO) {
    for (const path of paths) {
      path.style.fill = ''
    }
    return
  }

  // Normalize compartmentTypes to the inner map
  const typesMap = compartmentTypes?.compartments_types ?? compartmentTypes ?? {}

  // COMPARE pre-compute: per-type area arrays from allProducts for decile calc
  let compareDeciles = null
  if (scheme === COLOR_SCHEME.COMPARE) {
    compareDeciles = buildCompareDeciles(allProducts)
  }

  // Build a lookup: compartment number → product compartment data
  const byNumber = {}
  for (const c of productCompartments) {
    byNumber[c.number] = c
  }

  for (const path of paths) {
    const number = Number(path.getAttribute('number'))
    const productCompartment = byNumber[number]

    // nameCad: the 'id*' class on the path (e.g. 'idBedroom')
    const nameCad = [...path.classList].find((c) => c.startsWith('id')) ?? null
    const typeData = nameCad ? typesMap[nameCad] : null

    let fill = null

    if (scheme === COLOR_SCHEME.ZONES) {
      // Store var() strings (not resolved hex) so _toMutedFill can produce -muted variant.
      const location = typeData?.location ?? 'interior'
      if (location !== 'interior') {
        fill = 'var(--bim-color-zone-outdoor)'
      } else {
        const zone = typeData?.zone ?? 'neutral'
        fill = `var(--bim-color-zone-${zone})`
      }
    } else if (scheme === COLOR_SCHEME.TYPES) {
      const token = nameCad ? _nameCadToToken(nameCad) : null
      if (token) {
        const varName = `--bim-color-type-${token}`
        // Keep _getCssVar existence-check: undefined type tokens → no fill (CSS fallback).
        fill = _getCssVar(varName, rootElement) ? `var(${varName})` : null
      }
    } else if (scheme === COLOR_SCHEME.COMPARE) {
      const area = productCompartment?.area_original ?? null
      const nameCadForCompare = nameCad ?? null
      if (area != null && nameCadForCompare) {
        const bucket = getCompareDecile(area, nameCadForCompare, compareDeciles)
        fill = `var(--bim-color-compare-${bucket})`
      }
    } else if (scheme === COLOR_SCHEME.BRIGHTNESS) {
      const windowArea = productCompartment?.window_area ?? 0
      const floorArea  = productCompartment?.area_original ?? 0
      if (!windowArea) {
        // No windows — brightness not applicable; fall back to CSS default (room_default)
        fill = null
      } else {
        // ratio = (window_area / area_original) / (1/8) = window_area * 8 / area_original
        // ratio < 1.0 → below legal minimum → full blue (--bim-color-brightness-low)
        // ratio 1.0–3.0 → white → yellow (--bim-color-brightness-high), t linear
        // ratio > 3.0 → full yellow
        const ratioToMin = floorArea > 0 ? (windowArea / floorArea) * 8 : 0
        if (ratioToMin < 1) {
          fill = 'var(--bim-color-brightness-low)'
        } else {
          const t = Math.min(1, (ratioToMin - 1) / 2)
          const yellow = _getCssVar('--bim-color-brightness-high', rootElement)
          fill = yellow ? mixHex('#ffffff', yellow, t) : null
        }
      }
    }

    path.style.fill = fill ?? ''
    // Cache active fill so applyColorSchemeHoverHighlight can derive -muted without full re-run.
    if (fill) {
      path.dataset.schemeFill = fill
    } else {
      delete path.dataset.schemeFill
    }
  }
}

/**
 * Apply or clear the hover-highlight effect on compartment fills for InfoMode color schemes.
 *
 * When `matchingNumbers` is a Set (or array): paths whose number is NOT in the set
 * receive the `-muted` CSS var for their current scheme color; matching paths are
 * restored to their base active fill.
 *
 * When `matchingNumbers` is null: all paths are restored to their base active fill
 * (i.e. the fill set by the last `applyColorSchemeMutations` call).
 *
 * Only operates on non-MONO schemes — caller is responsible for gating.
 * Does not re-derive colors from scratch; reads the current `path.dataset.schemeFill`
 * which is written by {@link applyColorSchemeMutations} as a side-effect, so the
 * muted variant can be derived without re-running the full scheme logic.
 *
 * @param {SVGElement}           svgElement
 * @param {string}               scheme            — COLOR_SCHEME value (not MONO)
 * @param {Set<number>|number[]|null} matchingNumbers — compartment numbers to keep active; null = restore all
 */
export function applyColorSchemeHoverHighlight(svgElement, scheme, matchingNumbers) {
  if (scheme === COLOR_SCHEME.MONO) return

  const paths = svgElement.querySelectorAll('path.compartment')
  const matchSet = matchingNumbers == null ? null : new Set(matchingNumbers)

  for (const path of paths) {
    const activeFill = path.dataset.schemeFill ?? ''
    if (!activeFill) continue

    if (matchSet === null) {
      path.style.fill = activeFill
    } else {
      const number = Number(path.getAttribute('number'))
      path.style.fill = matchSet.has(number) ? activeFill : _toMutedFill(activeFill, scheme)
    }
  }
}

/**
 * Derives the `-muted` CSS var equivalent for a given active fill value.
 * Replaces the CSS var name inside the `var(…)` string with its -muted counterpart.
 * Falls back to the active fill if the pattern is not recognised.
 *
 * @param {string} activeFill — e.g. 'var(--bim-color-zone-day)'
 * @param {string} _scheme    — reserved for future multi-scheme disambiguation
 * @returns {string}
 */
function _toMutedFill(activeFill, _scheme) {
  // var(--bim-color-{family}-{key}) → var(--bim-color-{family}-{key}-muted)
  return activeFill.replace(/^var\((--bim-color-[^)]+)\)$/, 'var($1-muted)')
}

/**
 * Builds a lookup of per-type sorted area arrays from all products,
 * used for COMPARE decile computation.
 *
 * Expensive — iterates all products × compartments. Call once on mount or when
 * `allProducts` changes, then cache the result (e.g. in a React ref).
 * Consumers: {@link getCompareDecile}, {@link applyColorSchemeMutations} (internal).
 *
 * Returns a Map: nameCad → sorted number[] of area_original values
 *
 * @param {Array} allProducts
 * @returns {Map<string, number[]>}
 */
export function buildCompareDeciles(allProducts) {
  const map = new Map()
  for (const p of allProducts) {
    for (const c of p.compartments ?? []) {
      const key = c.name_cad
      if (!key || c.area_original == null) continue
      if (!map.has(key)) map.set(key, [])
      map.get(key).push(c.area_original)
    }
  }
  for (const [key, arr] of map) {
    map.set(key, arr.slice().sort((a, b) => a - b))
  }
  return map
}

/**
 * Returns one of 10 decile buckets ('p0'–'p9') for a given area,
 * relative to all compartments of the same type across all products.
 * Boundaries: 10th / 20th / ... / 90th percentile of the sorted area list.
 *
 * Use with a pre-built deciles map from {@link buildCompareDeciles} — cache it
 * in a ref so it is not rebuilt on every pointer move.
 *
 * @param {number} area
 * @param {string} nameCad
 * @param {Map<string, number[]>} deciles — pre-built by buildCompareDeciles
 * @returns {'p0'|'p1'|'p2'|'p3'|'p4'|'p5'|'p6'|'p7'|'p8'|'p9'}
 */
export function getCompareDecile(area, nameCad, deciles) {
  const arr = deciles.get(nameCad)
  if (!arr || arr.length === 0) return 'p4' // neutral: at avg
  for (let i = 1; i <= 9; i++) {
    const boundary = arr[Math.floor(arr.length * i / 10)]
    if (area < boundary) return `p${i - 1}`
  }
  return 'p9'
}
